import { createContext, Dispatch, SetStateAction, useContext } from 'react';

import noop, { NoOp } from 'utils/noop';

import {
  PanelView,
  defaultImgMetadata,
  imageAttrStateDefault,
  linkAttrStateDefault,
} from '../constants';
import {
  ImageAttrAction,
  ImageAttrState,
  ImageMetadata,
  LinkAttrAction,
  LinkAttrState,
  MovableInkBlock,
} from '../types';

export interface PanelContextState {
  canStyle: boolean;
  debouncedPanelSearch: string; // debounced value of search, used to reduce frequency of fetches
  imageAttrState: ImageAttrState; // located in the context so that it can persist between view switching (List <-> Details)
  imgMetadata: ImageMetadata;
  linkAttrState: LinkAttrState;
  nonCustomizableAttrs: Record<string, string>;
  onCancel: NoOp;
  onClose: NoOp;
  onInsert?: (imgAttrs: Record<string, string>, linkAttrs: Record<string, string>) => void;
  onSelect?: (url: string, imgMetadata?: ImageMetadata) => void; // returns url for text field (and optionally the image metadata)
  panelSearch: string;
  panelView: PanelView;
  scrollBookmark: number;
  selectedEditorImageNode?: HTMLImageElement; // a currently selected image node in the WYSIWYG editor (could be previous Asset Manager asset or remote image node)
  setImageAttrState: Dispatch<ImageAttrAction>;
  setLinkAttrState: Dispatch<LinkAttrAction>;
  setNonCustomizableAttrs: Dispatch<SetStateAction<Record<string, string>>>;
  setPanelSearch: (newPanelSearch: string) => void;
  setPanelView: (pv: PanelView) => void;
  setScrollBookmark: (pos: number) => void;
  updateImageAttribute: (block: MovableInkBlock) => void;
  setIsCampaignSelectorsSticky: (past: boolean) => void;
  isCampaignSelectorsSticky: boolean;
}

export const panelContextDefaults: PanelContextState = {
  canStyle: true,
  debouncedPanelSearch: '',
  imageAttrState: imageAttrStateDefault,
  imgMetadata: defaultImgMetadata,
  linkAttrState: linkAttrStateDefault,
  nonCustomizableAttrs: {},
  onCancel: noop,
  onClose: noop,
  onInsert: undefined,
  onSelect: undefined,
  panelSearch: '',
  panelView: PanelView.List,
  scrollBookmark: 0,
  selectedEditorImageNode: undefined,
  setImageAttrState: noop,
  setLinkAttrState: noop,
  setNonCustomizableAttrs: noop,
  setPanelSearch: noop,
  setPanelView: noop,
  setScrollBookmark: noop,
  updateImageAttribute: noop,
  setIsCampaignSelectorsSticky: noop,
  isCampaignSelectorsSticky: false,
};

export const PanelContext = createContext<PanelContextState>(panelContextDefaults);

export const usePanelContext = () => useContext(PanelContext);
